import React, { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { api, API, fmtMoney, extractErrorMessage } from "@/lib/api";
import { PayBadge } from "@/components/PayBadge";
import { PayInvoiceButton } from "@/components/PayInvoiceButton";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileText, Download, CreditCard } from "lucide-react";

function PayDialog({ invoice, clientId, environment, onClose, onPaid }) {
  const [working, setWorking] = useState(false);
  return (
    <Dialog open={!!invoice} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="paypal-pay-dialog">
        <DialogHeader>
          <DialogTitle>Pay invoice {invoice.invoice_number}</DialogTitle>
          <DialogDescription>Securely pay the outstanding balance for job #{invoice.job_number} via PayPal.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Job</span><span className="font-mono-arps">#{invoice.job_number}</span></div>
            <div className="mt-1 flex justify-between"><span className="text-slate-500">Amount due</span><span data-testid="paypal-amount-due" className="font-semibold text-slate-900">{fmtMoney(invoice.currency, invoice.balance_due)}</span></div>
          </div>
          {clientId ? (
          <PayPalScriptProvider options={{ clientId: clientId, currency: invoice.currency || "USD", intent: "capture", components: "buttons" }}>
            <PayPalButtons
              style={{ layout: "vertical", label: "pay" }}
              disabled={working}
              forceReRender={[invoice.id, invoice.balance_due]}
              createOrder={async () => {
                setWorking(true);
                try {
                  const { data } = await api.post(`/invoices/${invoice.id}/paypal/create-order`);
                  return data.paypal_order_id;
                } catch (e) {
                  setWorking(false);
                  toast.error(extractErrorMessage(e, "Could not start PayPal checkout."));
                  throw e;
                }
              }}
              onApprove={async (data) => {
                try {
                  const res = await api.post(`/invoices/${invoice.id}/paypal/capture`, { paypal_order_id: data.orderID });
                  if (res.data.payment_status === "paid") {
                    toast.success(`Invoice ${invoice.invoice_number} paid`);
                  } else {
                    toast.success("Payment recorded");
                  }
                  onPaid();
                } catch (e) {
                  toast.error(extractErrorMessage(e, "Payment could not be verified."));
                } finally { setWorking(false); }
              }}
              onCancel={() => { setWorking(false); toast.message("Payment cancelled"); }}
              onError={() => { setWorking(false); toast.error("PayPal error. Please try again."); }}
            />
          </PayPalScriptProvider>
          ) : (
            <p className="text-center text-sm text-amber-600" data-testid="paypal-not-configured">PayPal is not configured. Please contact support.</p>
          )}
          <p className="text-center text-xs text-slate-400">
            {environment === "sandbox" ? "PayPal Sandbox (test mode)" : "Secured by PayPal"}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ClientInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payCfg, setPayCfg] = useState({ configured: false, client_id: "", environment: "sandbox" });
  const [payInvoice, setPayInvoice] = useState(null);

  const loadInvoices = () => api.get("/invoices").then(({ data }) => setInvoices(data)).finally(() => setLoading(false));
  useEffect(() => {
    loadInvoices();
    api.get("/paypal/config").then(({ data }) => setPayCfg(data)).catch(() => {});
  }, []);

  const outstanding = invoices.reduce((s, i) => s + (i.balance_due || 0), 0);
  const currency = invoices[0]?.currency || "USD";

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Billing &amp; invoices</h2>
        <p className="text-slate-500">Service charges, secure payment, and downloadable receipts.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <p className="text-sm text-slate-500">Invoices</p>
          <p data-testid="client-invoice-count" className="font-display text-3xl font-extrabold text-slate-900">{invoices.length}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <p className="text-sm text-slate-500">Outstanding balance</p>
          <p data-testid="client-outstanding" className="font-display text-3xl font-extrabold text-amber-600">{fmtMoney(currency, outstanding)}</p>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3">Invoice #</th><th className="px-5 py-3">Job #</th>
              <th className="px-5 py-3">Total</th><th className="px-5 py-3">Balance</th>
              <th className="px-5 py-3">Status</th><th className="px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {invoices.map((i) => {
              const unpaid = i.payment_status !== "paid" && (i.balance_due || 0) > 0;
              return (
              <tr key={i.id} data-testid={`client-invoice-row-${i.invoice_number}`} className="transition hover:bg-slate-50">
                <td className="px-5 py-3 font-mono-arps font-semibold text-slate-900">{i.invoice_number}</td>
                <td className="px-5 py-3 text-slate-500">#{i.job_number}</td>
                <td className="px-5 py-3 font-semibold text-slate-900">{fmtMoney(i.currency, i.total)}</td>
                <td className="px-5 py-3 text-slate-600">{fmtMoney(i.currency, i.balance_due)}</td>
                <td className="px-5 py-3"><PayBadge status={i.payment_status} /></td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-48"><PayInvoiceButton invoice={i} onPaid={loadInvoices} /></div>
                    <a data-testid={`view-receipt-${i.invoice_number}`} href={`${API}/invoices/${i.id}/receipt`} target="_blank" rel="noreferrer">
                      <Button size="sm" variant="outline"><Download className="mr-1.5 h-3.5 w-3.5" /> Receipt</Button>
                    </a>
                  </div>
                </td>
              </tr>
            );})}
            {!loading && invoices.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-14 text-center text-slate-400">
                <FileText className="mx-auto mb-2 h-6 w-6 text-slate-300" />
                No invoices yet. Invoices are generated when a service is completed and approved.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}
